<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { $api } from '@/services';

const store = useStore();
const formData = ref({});
const skills = ref([]);
const stats = ref({});
const route = useRoute();
const isLoading = ref(true);
const openMenu = ref(false);

onMounted(() => {
	fetchData();
});

const fetchData = async () => {
	try {
		const referenceQuery =
			route.query.is_reference === 'true' ? '?is_reference=true' : '';
		const response = await fetch(
			process.env.VUE_APP_BASE_URL +
				`v1/org_admin/assessment_forms/${route.params.id}${referenceQuery}`,
			{
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					'x-access-token': route?.query?.token,
					'x-subdomain': route?.query?.subdomain,
				},
			}
		);
		const result = await response.json();
		formData.value = result.data;
		skills.value = result.skills;
		stats.value = result.stats;
		isLoading.value = false;
	} catch (err) {
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		isLoading.value = false;
		if (route?.query?.is_print === 'true') {
			setTimeout(() => {
				window.print();
			}, 3000);
		}
	}
};
</script>
<template>
	<div class="px-12 md-down:px-4 pb-10" id="form_pdf">
		<div class="__report-lesson__header pt-10 pb-10">
			<div
				class="
					flex
					items-center
					gap-3
					mb-2
					text-neutral-500 text-sm
					font-semibold
				"
			>
				<div
					class="cursor-pointer"
					@click="$router.push('/form-evaluation')"
				>
					Biểu mẫu đánh giá
				</div>
				<img alt="" src="@/assets/images/icons/arrow-right.png" />
				<div>{{ formData?.name }}</div>
			</div>
			<div class="flex flex-nowrap justify-between items-center mb-2">
				<div
					class="
						__record__title
						text-neutral-700
						font-semibold
						whitespace-nowrap
						flex flex-nowrap
						items-center
						text-4xl
						gap-4
					"
				>
					<svg
						class="cursor-pointer"
						width="28"
						height="28"
						viewBox="0 0 28 28"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M17.5 7L10.5 14L17.5 21"
							stroke="#94A3B8"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
					<img
						src="@/assets/images/icons/form-evaluation.png"
						alt=""
					/>
					<div>{{ formData?.name }}</div>
				</div>
			</div>
			<div class="text-green-500 text-xs font-semibold">
				TẠO BỞI
				<span class="text-blue-600 text-sm">{{
					formData?.created_by_staff?.full_name
				}}</span>
				| LÚC
				{{ $filters.formatDateTime(formData.inserted_at) }}
			</div>
		</div>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
			<div
				class="
					flex
					items-center
					justify-between
					bg-white
					py-4
					px-6
					rounded-lg
					shadow
				"
			>
				<div class="flex items-center gap-2">
					<img
						alt=""
						height="32"
						src="@/assets/images/icons/abc.png"
						style="height: 32px"
						width="32"
					/>
					<div class="text-neutral-500 text-sm">Lĩnh vực</div>
				</div>
				<div class="font-bold text-4xl text-neutral-600">
					{{ stats?.skill_count }}
				</div>
			</div>
			<div
				class="
					flex
					items-center
					justify-between
					bg-white
					py-4
					px-6
					rounded-lg
					shadow
				"
			>
				<div class="flex items-center gap-2">
					<img
						alt=""
						height="32"
						src="@/assets/images/icons/light.png"
						style="height: 32px"
						width="32"
					/>
					<div class="text-neutral-500 text-sm">Mục tiêu</div>
				</div>
				<div class="font-bold text-4xl text-neutral-600">
					{{ stats?.goal_count }}
				</div>
			</div>
		</div>
		<div v-for="(s, index) in skills" :key="index" class="mb-4">
			<div class="flex items-center justify-between mb-4">
				<div class="flex items-center">
					<div class="font-semibold text-neutral-700">
						{{ index + 1 }}. {{ s.skill?.name }} ({{
							s.library_items.length
						}})
					</div>
				</div>
			</div>
			<div
				class="shadow-sm sm:rounded-2xl overflow-hidden md-down:hidden"
			>
				<div class="__record__table overflow-x-auto scroll-bar">
					<div
						class="
							overflow-hidden
							sm:rounded-t-2xl
							w-fit
							min-w-full
						"
					>
						<table class="min-w-full divide-y text-neutral-200">
							<thead class="bg-neutral-100 h-16">
								<tr>
									<th
										class="
											px-4
											py-3
											text-center text-xs
											font-semibold
											text-neutral-600
											uppercase
											tracking-wider
											whitespace-nowrap
											w-16
										"
										scope="col"
									>
										STT
									</th>
									<th
										class="
											px-6
											py-3
											text-left text-xs
											font-semibold
											text-neutral-600
											uppercase
											tracking-wider
											whitespace-nowrap
										"
										scope="col"
									>
										Nội dung
									</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(i, gIndex) in s.library_items"
									:key="gIndex"
									:class="[
										gIndex % 2 === 0
											? 'bg-white'
											: 'bg-neutral-50',
									]"
									class="text-neutral-700 hover:bg-orange-50"
								>
									<td
										class="
											p-4
											whitespace-nowrap
											text-neutral-700 text-center
										"
									>
										{{ gIndex + 1 }}
									</td>
									<td
										class="
											px-6
											py-4
											whitespace-nowrap
											text-neutral-700
										"
									>
										<div class="flex items-center gap-2">
											<div
												class="whitespace-normal"
												v-html="i.name || i.goal_name"
											></div>
											<!-- <TooltipGoalDetail
												:goal-id="i.goal_id || i.id"
												:student-id="studentId"
											/> -->
										</div>
										<div
											class="flex gap-2 flex-wrap"
											style="max-width: 360px"
										>
											<template
												v-if="i?.assistance_levels"
											>
												<div
													v-for="ass in i?.assistance_levels"
													:key="ass"
													class="flex gap-2"
												>
													<div
														class="
															mt-1
															py-0.5
															px-2.5
															rounded-full
															bg-pink-100
															whitespace-nowrap
															text-neutral-700
															h-fit
															w-fit
															font-semibold
															shadow-input
														"
														style="font-size: 10px"
													>
														{{ ass }}
													</div>
												</div>
											</template>
											<div
												class="
													mt-1
													py-0.5
													px-2.5
													rounded-full
													bg-yellow-tag
													text-neutral-700
													h-fit
													w-fit
													font-semibold
													shadow-input
													whitespace-nowrap
												"
												style="font-size: 10px"
												v-if="i?.age"
											>
												{{ i?.age }}
											</div>
										</div>
										<div
											v-if="i.parent?.sub_goal_count > 0"
											class="goal-block flex gap-3 mt-2"
										>
											<img
												class="w-3 h-3 mt-1"
												src="@/assets/images/icons/goal-arrow.png"
												alt=""
											/>
											<div>
												<div
													class="
														text-neutral-700
														whitespace-normal
													"
													v-html="i.parent?.goal_name"
												></div>
												<div
													class="
														text-neutral-400 text-xs
														font-semibold
													"
												>
													{{
														i.parent?.sub_goal_count
													}}
													MỤC TIÊU
												</div>
											</div>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<style scoped>
.menu-action {
	z-index: 100;
	top: 45px;
	right: 0px;
	border-radius: 6px;
	border: 1px solid #f9e3b7;
	background: #fff;
	width: 224px;
	box-shadow: 0px 10px 16px 0px rgba(20, 37, 63, 0.06),
		0px 0px 1px 0px rgba(12, 26, 75, 0.1);
}
</style>
